import {
  type LayeringType, type MapInfo, type MapSnapshot, type ParentMap, type RealSpreadsheetVersions,
  type SpreadsheetDescriptor,
} from '~/_shared/types/map';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { createMapListingItemFromMapBaseMapResponse } from '~/map/map.factory';
import {
  type GoogleSheetBasedSpreadsheetInfoResponse, type LayeringResponse, type MapInfoResponse, type MapSnapshotResponse,
  type SpreadsheetDescriptorResponse, type VersionsResponse,
} from '~/map/map.repository';
import { createBoundaryCreateOptimizedStatusFromResponse } from '~/store/frontendState/mapTools/boundary/boundaryCreateOptimized/boundaryCreateOptimizedStatus.factory';
import { createBoundaryCreateWMSOptimizedStatusFromResponse } from '~/store/frontendState/mapTools/boundary/boundaryCreateWMSOptimized/boundaryCreateWMSOptimizedStatus.factory';

export const createRealSpreadSheetFromMapRealSpreadSheetResponse = (realSpreadSheet: {
  real_spreadsheet_id: number;
  salesforce_url?: string;
}) => ({
  realSpreadSheetId: realSpreadSheet.real_spreadsheet_id,
  ...(realSpreadSheet.salesforce_url ? { addRowLink: realSpreadSheet.salesforce_url } : {}),
});

export const createRealSpreadsheetVersionsFromMapResponse = (
  realSpreadsheetVersionResponses: VersionsResponse, layeringResponse: LayeringResponse
): ReadonlyArray<RealSpreadsheetVersions> => {
  if (!realSpreadsheetVersionResponses.data_changed) {
    return [];
  }

  return layeringResponse.base_maps.map((baseMap): RealSpreadsheetVersions => {
    const name = baseMap.name;
    if (baseMap.spreadsheets.length > 1 || (baseMap.spreadsheets[0] && baseMap.spreadsheets[0].real_spreadsheets.length > 1)) {
      throw Error('Unexpected to have more than 1 spreadsheet');
    }
    const realSpreadsheetId = baseMap.spreadsheets[0]?.real_spreadsheets[0]?.real_spreadsheet_id;
    const realSpreadsheetVersion = realSpreadsheetId && realSpreadsheetVersionResponses.real_spreadsheets[realSpreadsheetId];
    if (!realSpreadsheetVersion) {
      throw Error(`Cannot find version for real spreadsheet ${realSpreadsheetId}`);
    }
    const { versions: { current, latest } } = realSpreadsheetVersion;

    return {
      realSpreadSheetId: realSpreadsheetId,
      name,
      versions: {
        current: {
          version: current.version,
          snapshot: current.snapshot,
        },
        latest: {
          version: latest.version,
          snapshot: latest.snapshot,
        },
      },
    };
  });
};

export const createMapSpreadSheetFromMapSpreadSheetResponse = (
  spreadsheet: SpreadsheetDescriptorResponse
): SpreadsheetDescriptor => ({
  spreadSheetId: spreadsheet.spreadsheet_id,
  isEncrypted: spreadsheet.is_encrypted,
  isPrimary: spreadsheet.is_primary,
  importing: spreadsheet.importing,
  privileges: spreadsheet.privileges,
  realSpreadsheets: spreadsheet.real_spreadsheets.map(realSpreadsheet => createRealSpreadSheetFromMapRealSpreadSheetResponse(realSpreadsheet)),
  geocoding: spreadsheet.geocoding,
  rowRelations: spreadsheet.row_relations,
});

export const createLayeringFromResponse = (layering?: LayeringResponse): LayeringType | undefined => {
  if (!layering) {
    return undefined;
  }

  const baseMaps = layering.base_maps
    .map(baseMap => createMapListingItemFromMapBaseMapResponse(baseMap))
    .filter(notNullsy);

  return {
    additional: layering.additional,
    baseMaps,
    columns: layering.columns,
    connected: layering.connected,
    sourceColumns: layering.source_columns,
    matches: layering.matches.map(match => ({ ...match, columnId: match.column_id })),
    realTime: layering.real_time,
  };
};

export const createParentMapFromParentMapResponse = (data?: MapInfoResponse): ParentMap | undefined => {
  if (!data) {
    return undefined;
  }

  const baseMap = createMapInfoFromMapInfoResponse(data);

  return {
    ...baseMap,
    parentMap: undefined,
    snapshots: data.snapshots?.map(view => createMapSnapshotFromResponse(view)) || [],
  };
};

export const createMapSnapshotFromResponse = (snapshot: MapSnapshotResponse): MapSnapshot => {
  const baseMap = createMapInfoFromMapInfoResponse({ ...snapshot, parent_map: undefined });

  return {
    ...baseMap,
    parentMap: snapshot.parent_map,
    snapshots: undefined,
  };
};

const getGoogleSheetsInfoFromMapInfoGoogleSheetsResponse = (mapInfoGoogleSheets: ReadonlyArray<GoogleSheetBasedSpreadsheetInfoResponse>) => {
  if (mapInfoGoogleSheets) {
    return mapInfoGoogleSheets.map((ssItem) => ({
      spreadsheetId: ssItem.spreadsheet_id,
      realSpreadsheets: ssItem.real_spreadsheets.map((rsItem) => ({
        autoSyncEnabled: rsItem.auto_sync_enabled,
        googleAccountId: rsItem.google_account_id,
        googleSheetId: rsItem.google_sheet_id,
        googleSpreadsheetId: rsItem.google_spreadsheet_id,
        lastSyncType: rsItem.last_sync_type,
        realSpreadsheetId: rsItem.real_spreadsheet_id,
        spreadsheetTitle: rsItem.spreadsheet_title,
      })),
    }));
  }
  return null;
};

export const createMapInfoFromMapInfoResponse = (
  data: MapInfoResponse
): MapInfo => ({
  created: data.created,
  description: data.description,
  encrypted: data.encrypted,
  publicExportDataAllowed: !!data.export_data_allowed,
  googleSheets: getGoogleSheetsInfoFromMapInfoGoogleSheetsResponse(data.google_sheets),
  id: data.id,
  importing: data.importing,
  isLayered: data.is_layered,
  isMatchupRequired: data.is_matchup_required,
  isReadOnly: data.read_only,
  layering: createLayeringFromResponse(data.layering),
  name: data.name,
  parentMap: createParentMapFromParentMapResponse(data.parent_map),
  privacy: data.privacy,
  secureInProcess: data.secure_in_process,
  settings: data.settings,
  sharedUsers: data.shared_users,
  shareId: data.share_id,
  snapshots: data.snapshots?.map(snapshot => createMapSnapshotFromResponse(snapshot)),
  spreadsheets: data.spreadsheets.map((spreadsheet: SpreadsheetDescriptorResponse) => createMapSpreadSheetFromMapSpreadSheetResponse(spreadsheet)),
  team: data.team,
  territoryGeneration: (data.territories_generation ?? []).map(item => {
    if (item.type === 'WMS') {
      return {
        type: 'WMS',
        id: item.id,
        isFailed: !!item.is_failed,
        status: createBoundaryCreateWMSOptimizedStatusFromResponse(item.status),
        startedOnCurrentTab: false,
      };
    }
    else {
      return {
        type: 'Maptive',
        hash: item.hash,
        isFailed: !!item.is_failed,
        status: createBoundaryCreateOptimizedStatusFromResponse(item.status),
        startedOnCurrentTab: false,
      };
    }
  }),
  updated: data.updated,
  versions: (data.versions && data.layering) ? createRealSpreadsheetVersionsFromMapResponse(data.versions, data.layering) : undefined,
  views: data.views,
  v4MigrationDone: data.v4_migration_done,
  source: data.source ?? null,
});
